import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { TrendingUp, ArrowRight, Gift } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", referral_code: "" });
  const [loading, setLoading] = useState(false);

  // Prefill referral code from ?ref=CODE (share link) and remember it across page reloads
  useEffect(() => {
    const fromUrl = (params.get("ref") || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 16);
    const cached = (localStorage.getItem("aanar_referral_code") || "").toUpperCase();
    const code = fromUrl || cached;
    if (fromUrl) localStorage.setItem("aanar_referral_code", fromUrl);
    if (code) setForm((f) => ({ ...f, referral_code: code }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.referral_code) delete payload.referral_code;
      await register(payload);
      localStorage.removeItem("aanar_referral_code");
      toast.success("Account created — $100,000 virtual cash added");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative bg-card border-r border-border overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-2">
            <img src="/brand/aanar-mark-reversed.svg" alt="Aanar" className="w-10 h-10 dark:block hidden" />
            <img src="/brand/aanar-mark.svg" alt="Aanar" className="w-10 h-10 dark:hidden block" />
            <div className="font-display font-bold text-xl">Aanar</div>
          </div>
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--ai))] mb-3">Learn by doing</div>
            <h1 className="font-display text-5xl font-bold leading-tight mb-4">
              Zero risk.<br/>Real markets.
            </h1>
            <p className="text-muted-foreground">
              Start with $100,000 virtual cash. Trade crypto and stocks with live prices. Get AI insights drawn from Market Wizards, Reminiscences, and more.
            </p>
          </div>
          <div className="text-xs text-muted-foreground italic">"The market can remain irrational longer than you can remain solvent." — John Maynard Keynes</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="register-form">
          <div>
            <h2 className="font-display text-3xl font-bold">Create account</h2>
            <p className="text-sm text-muted-foreground mt-1">Get $100k in virtual cash to practice.</p>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="reg-name">Full name</Label>
              <Input id="reg-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                     required autoComplete="name" data-testid="register-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-email">Email</Label>
              <Input id="reg-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                     required autoComplete="username" data-testid="register-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-password">Password</Label>
              <Input id="reg-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                     required minLength={8} autoComplete="new-password" data-testid="register-password" />
              <p className="text-xs text-muted-foreground">8+ characters, at least one letter and one digit</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg-ref" className="flex items-center gap-1.5">
                <Gift className="w-3.5 h-3.5" /> Referral code <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input
                id="reg-ref"
                value={form.referral_code}
                onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 16) })}
                placeholder="e.g. AB12CD34"
                autoComplete="off"
                data-testid="register-referral"
                className="font-mono-tab tracking-wider"
              />
              {form.referral_code && (
                <p className="text-xs text-[hsl(var(--gain))]" data-testid="register-referral-hint">
                  Your inviter will earn $10 when you publish your first Bronze bot.
                </p>
              )}
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="register-submit">
            {loading ? "Creating..." : "Create account"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-foreground font-medium hover:underline" data-testid="link-login">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
